import React, { Dispatch, FormEvent, SetStateAction } from 'react';
import { UseBoolean } from 'react-hanger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GridSettingsMenuItem from '@components-clean/core/grid/grid-menu/grid-settings-menu/grid-settings-menu-item';
import { GridColumnProps } from '@components-clean/core/grid/grid.interface';
import { useBoolean, useTranslation } from '@hooks/index';

interface GridLockMenuProps {
    columns: GridColumnProps[];
    updateColumns: Dispatch<SetStateAction<GridColumnProps[]>>;
    menuExpand: UseBoolean;
    closeSettingsMenu?: () => void;
    lockedColumnsUpdated: ReturnType<typeof useBoolean>;
}

const lockColumnChildren = (columns: GridColumnProps[] | undefined, state: boolean): GridColumnProps[] | undefined => {
    if (!columns) {
        return undefined;
    }

    return columns.map((column) => ({
        ...column,
        locked: state,
        children: lockColumnChildren(column.children, state),
    }));
};

const GridLockMenu = ({
    columns,
    updateColumns,
    closeSettingsMenu,
    menuExpand,
    lockedColumnsUpdated,
}: GridLockMenuProps) => {
    const { t } = useTranslation();
    const [currentColumns, setCurrentColumns] = React.useState<GridColumnProps[]>(columns);
    const onToggleColumn = (id: number) => {
        const newColumns = currentColumns.map((column, idx) => {
            return idx === id
                ? {
                      ...column,
                      locked: !column.locked,
                      children: lockColumnChildren(column.children, !column.locked),
                  }
                : column;
        });
        setCurrentColumns(newColumns);
    };

    const onToggleChildColumn = (id: number, childId: number) => {
        const newColumns = currentColumns.map((column, idx) =>
            id === idx
                ? {
                      ...column,
                      children: column.children
                          ? [
                                ...column.children.map((child, childIdx) =>
                                    childIdx === childId ? { ...child, locked: !child.locked } : child,
                                ),
                            ]
                          : undefined,
                  }
                : column,
        );
        const parentColumn = newColumns[id];
        if (parentColumn && parentColumn.children) {
            const childCount = parentColumn.children.length;
            if (childCount > 0)
                parentColumn.locked = parentColumn.children.filter((c) => c.locked).length === childCount;
        }
        setCurrentColumns(newColumns);
    };

    const onReset = (event: FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        const newColumns = currentColumns.map((column) => ({
            ...column,
            locked: column.lockedByDefault ?? false,
            children: lockColumnChildren(column.children, column.lockedByDefault ?? false),
        }));

        setCurrentColumns(newColumns);
        updateColumns(newColumns);
        lockedColumnsUpdated.setTrue();

        if (closeSettingsMenu) {
            closeSettingsMenu();
        }
    };

    const onSubmit = (event: FormEvent<HTMLFormElement>) => {
        if (event) {
            event.preventDefault();
        }

        updateColumns(currentColumns);
        lockedColumnsUpdated.setTrue();

        if (closeSettingsMenu) {
            closeSettingsMenu();
        }
    };

    return (
        <GridSettingsMenuItem
            columns={currentColumns}
            onSubmit={onSubmit}
            onToggle={onToggleColumn}
            onToggleChild={onToggleChildColumn}
            onReset={onReset}
            fieldName="locked"
            buttonProps={{
                icon: <FontAwesomeIcon icon={['far', 'lock']} fontSize={15} />,
                label: `${t('lockColumns')}`,
            }}
            menuExpand={menuExpand}
            type="lock"
        />
    );
};

export default GridLockMenu;
